import React, { memo, useCallback, useRef, useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { areEqual } from 'Utils/equalityChecks';

import { RocketDryRoomHeader, LocationEquipment } from 'Components/RocketDry';
import { SpinnerBlock } from 'Components/SpinnerBlock';

import { getRoomEquipmentList } from 'Containers/RocketDry/actions';

import { useRocketDryFunctions } from 'Context/RocketDry';

interface Props {
  room: any;
}

const EquipmentLogRoomContainer = ({ room }: Props) => {
  const dispatch = useDispatch();

  const mounted = useRef(true);

  const [equipmentList, setEquipmentList] = useState([]);
  const [fetching, setFetching] = useState(false);

  // room data
  const {
    id: roomId,
    room_type: { name: roomType },
  } = room;

  const {
    setIsAddEquipmentModalOpen,
    setSelectedRoomId,
    equipmentAdded,
    setEquipmentAdded,
    selectedRoomId,
    equipmentUpdatedRoomId,
    setEquipmentUpdatedRoomId,
    equipmentDeletedRoomId,
    setEquipmentDeletedRoomId,
  }: any = useRocketDryFunctions();

  const getEquipmentList = useCallback(async () => {
    setFetching(true);

    const response: any = await dispatch(getRoomEquipmentList(roomId));

    if (mounted) {
      if (response?.data) {
        const { data } = response;
        setEquipmentList(data);
      } else {
        setEquipmentList([]);
      }

      setFetching(false);
    }
  }, [roomId]);

  const onAddButtonClick = useCallback(() => {
    setSelectedRoomId(roomId);
    setIsAddEquipmentModalOpen(true);
  }, [roomId]);

  // initial fetch
  useEffect(() => {
    mounted.current = true;
    (async function fetchData() {
      await getEquipmentList();
    })();

    return () => {
      if (mounted) {
        mounted.current = false;
      }
    };
  }, []);

  // fetch when equipment added
  useEffect(() => {
    if (equipmentAdded && selectedRoomId === roomId) {
      (async function fetchData() {
        await getEquipmentList();
        setEquipmentAdded(false);
      })();
    }
  }, [equipmentAdded, selectedRoomId, roomId]);

  // fetch when equipment updated
  useEffect(() => {
    if (equipmentUpdatedRoomId === roomId) {
      (async function fetchData() {
        await getEquipmentList();
        setEquipmentUpdatedRoomId(undefined);
      })();
    }
  }, [equipmentUpdatedRoomId, roomId]);

  // fetch when equipment updated
  useEffect(() => {
    if (equipmentDeletedRoomId === roomId) {
      (async function fetchData() {
        await getEquipmentList();
        setEquipmentDeletedRoomId(undefined);
      })();
    }
  }, [equipmentDeletedRoomId, roomId]);

  return (
    <div>
      <RocketDryRoomHeader icon={roomType} title={roomType} />
      <br />
      <SpinnerBlock fetching={fetching} />
      {!fetching && <LocationEquipment equipmentList={equipmentList} onAddButtonClick={onAddButtonClick} />}
    </div>
  );
};

const EquipmentLogRoomContainerMemo = memo(EquipmentLogRoomContainer, areEqual);

export { EquipmentLogRoomContainerMemo as EquipmentLogRoom };
